import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter, KeyboardSensor, useDraggable, useDroppable, pointerWithin } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';

import { twMerge } from 'tailwind-merge';
import { HiSearch, HiX } from 'react-icons/hi';
import { FaPlus, FaUsers } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TbCalendar, TbChevronDown, TbEdit, TbGripVertical, TbSlideshow, TbTrash } from 'react-icons/tb';
import { useLocation } from 'react-router-dom';
import PageHeader from '../../components/misc/PageHeader';
import PageWithTabsLayout from '../../components/layouts/PageWithTabsLayout';
import Button from '../../components/buttons/Button';
import { taskService } from '../../../services/community/taskService';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { Task, TaskStatus } from '../../../models/taskService.model';
import connectionService from '../../../services/community/connectionService';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../../components/layouts/ModalLayout';
import Labeled from '../../components/misc/Labeled';
import DatePicker from '../../components/forms/DatePicker';
import { formatDateAndTime } from '../../../utils/stringUtils';
import Input from '../../components/misc/Input';
import Avatar from '../../components/misc/Avatar';
import { RootState } from '../../../redux/reducers';
import useIsScreenSize from '../../../hooks/effects/useIsScreenSize';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import useTaskStatuses from '../../../hooks/community/useTaskStatuses';
// eslint-disable-next-line import/extensions
import TaskBoard from './TaskBoard';

type ViewMode = 'list' | 'board';

const isSystemStatus = (status: TaskStatus): boolean => Boolean(status.systemKey);
const isOpenStatus = (status?: TaskStatus): boolean => status?.systemKey === 'OPEN';
const isClosedStatus = (status?: TaskStatus): boolean => status?.systemKey === 'CLOSED';

const getStatusLabel = (status: TaskStatus, t: (key: string) => string): string => {
  if (status.systemKey === 'OPEN') return t('defaultOpenStatus');
  if (status.systemKey === 'CLOSED') return t('defaultClosedStatus');
  return status.name;
};

export default function TaskOverviewPage() {
  const community = useSelectedCommunity();
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const loc = useLocation();

  const [taskList, setTaskList] = useState<Task[]>([]);
  const [name, setName] = useState<string | undefined>();
  const [taskDescription, setTaskDescription] = useState<string | undefined>();
  const [dueDate, setDueDate] = useState<Date>();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [open, setOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [visibleTasks, setVisibleTasks] = useState<Task[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<Connection[]>([]);
  const [taskToUpdate, setTaskToUpdate] = useState<Task>();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedStatusId, setSelectedStatusId] = useState<number | undefined>();
  const [editStatusId, setEditStatusId] = useState<number | undefined>();
  const [manageStatusesOpen, setManageStatusesOpen] = useState(false);
  const [activeListTask, setActiveListTask] = useState<Task | null>(null);
  const [showClosedTasks, setShowClosedTasks] = useState(false);

  const { statuses, createStatus, updateStatus, deleteStatus, reload } = useTaskStatuses(community?.id);

  const listSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const filterVisibleTasks = (tasks: Task[]) => (
    showClosedTasks ? tasks : tasks.filter((task) => !isClosedStatus(task.status))
  );

  const visibleStatuses = statuses.filter((status) => showClosedTasks || !isClosedStatus(status));

  useEffect(() => {
    if (loc.state?.createOpen) {
      setOpen(true);
    }

    if (community?.id) {
      taskService.getAllTasksForCommunity(community.id).then((tasks) => {
        setTaskList(tasks);
      });
      connectionService
        .getConnections()
        .then((x) => x.filter((c) => c.type === ConnectionType.MEMBER))
        .then(setConnections);
    }
  }, []);

  useEffect(() => {
    setVisibleTasks(filterVisibleTasks(taskList));
  }, [taskList, showClosedTasks]);

  // when statuses are available and no status is selected yet, default to OPEN
  useEffect(() => {
    if (statuses.length > 0 && selectedStatusId === undefined) {
      setSelectedStatusId(statuses.find((status) => isOpenStatus(status))?.statusId ?? statuses[0].statusId);
    }
  }, [statuses, selectedStatusId]);

  useEffect(() => {
    if (taskToUpdate) {
      setTaskToUpdate(visibleTasks.find((x) => x.taskId === taskToUpdate.taskId));
    }
  }, [visibleTasks]);

  useEffect(() => {
    if (!open) {
      setAssignedUsers([]);
      setDueDate(undefined);
      setName(undefined);
      setTaskDescription(undefined);
      setSelectedStatusId(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!updateModalOpen) {
      setDueDate(undefined);
      setName(undefined);
      setTaskDescription(undefined);
      setEditStatusId(undefined);
    } else if (taskToUpdate) {
      setDueDate(taskToUpdate.dueDate ? new Date(taskToUpdate.dueDate) : undefined);
      setName(taskToUpdate.name);
      setTaskDescription(taskToUpdate.description);
      setEditStatusId(
        taskToUpdate.status?.statusId ?? (statuses.length > 0 ? statuses[0].statusId : undefined),
      );
    }
  }, [updateModalOpen, taskToUpdate, statuses]);

  const createTask = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (community?.id && name) {
      taskService
        .createTask(
          community.id,
          name,
          dueDate,
          assignedUsers.map((x) => x.memberId!),
          selectedStatusId,
          taskDescription,
        )
        .then(() => {
          setOpen(false);
          taskService.getAllTasksForCommunity(community.id!).then((tasks) => {
            setTaskList(tasks);
          });
        });
    } else {
      toast.error(t('errorInput'));
    }
  };

  const refreshTable = () => {
    if (community?.id) {
      taskService.getAllTasksForCommunity(community.id).then(setTaskList);
    }
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        secondaryButton={
          <div className="flex items-center gap-2">
            <Button
              variant='secondary'
              onClick={() => setShowClosedTasks((prev) => !prev)}>
              {showClosedTasks ? t('hideClosed') : t('showClosed')}
            </Button>
            <Button
              variant='secondary'
              onClick={() => setManageStatusesOpen(true)}>
              <TbSlideshow className="h-4 w-4" />
              {t('manageStatuses')}
            </Button>
          </div>
        }
        primaryButton={
          <PrimaryButton onClick={() => setOpen(true)} text={t('create')} icon={FaPlus} />
        }
      />

      {/* Create modal */}
      <ModalLayout
        closeButton
        open={open}
        setOpen={setOpen}
        preventClose={manageStatusesOpen}
        className="z-50 mx-6 my-8 w-full rounded-2xl bg-secondary-50 sm:max-w-[640px]">
        <div className="border-b border-secondary-200 px-8 py-5">
          <h2 className="text-lg font-semibold text-primary-900">{t('create')}</h2>
        </div>
        <form onSubmit={createTask} className="flex flex-col gap-5 p-8">
          <Labeled label={t('name')}>
            <Input
              type="text"
              placeholder={t('namePlaceholder')}
              name="name"
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </Labeled>
          <Labeled label={t('description')}>
            <Input
              type="textarea"
              placeholder={t('descriptionPlaceholder')}
              name="description"
              className="resize-y"
              onChange={(e) => setTaskDescription(e.currentTarget.value)}
            />
          </Labeled>
          <div className="flex w-full items-start gap-6 max-md:flex-col">
            <Labeled label={t('dueDate')}>
              <DatePicker date={dueDate} setDate={setDueDate} />
            </Labeled>
            <Labeled label="Assignee(s)" className="flex-1">
              <AssigneesMenu
                assignees={assignedUsers.map((x) => ({
                  userId: x.memberId!,
                  firstName: x.fields.FIRST_NAME,
                  lastName: x.fields.LAST_NAME,
                }))}
                assignMember={(userId) => {
                  const connection = connections.find((c) => c.memberId === userId);
                  if (connection && !assignedUsers.some((a) => a.memberId === userId)) {
                    setAssignedUsers([...assignedUsers, connection]);
                  }
                }}
                removeMember={(userId) =>
                  setAssignedUsers(assignedUsers.filter((a) => a.memberId !== userId))
                }
              />
            </Labeled>
          </div>
          <Labeled label={t('status')}>
            <StatusPicker
              statuses={statuses}
              selectedStatusId={selectedStatusId}
              onSelect={(id) => setSelectedStatusId(id)}
              onManage={() => setManageStatusesOpen(true)}
            />
          </Labeled>
          <div className="flex justify-end border-t border-secondary-200 pt-4">
            <Button type="submit" variant="primary">
              {t('create')}
            </Button>
          </div>
        </form>
        <ManageStatusesModal
          open={manageStatusesOpen}
          setOpen={setManageStatusesOpen}
          statuses={statuses}
          tasks={taskList}
          onCreate={(createName, color) => createStatus(createName, color)}
          onUpdate={(id, data) => updateStatus(id, data)}
          onDelete={(id) => deleteStatus(id)}
          onReorder={(id, order) => updateStatus(id, { order })}
          onReload={reload}
        />
      </ModalLayout>

      {/* Update modal */}
      {taskToUpdate && (
        <ModalLayout
          closeButton
          open={updateModalOpen}
          setOpen={setUpdateModalOpen}
          preventClose={manageStatusesOpen}
          className="z-50 mx-6 my-8 w-full overflow-hidden rounded-2xl bg-secondary-50 sm:max-w-[640px]">
        <div className="border-b border-secondary-200 bg-secondary-50 px-8 py-5">
            <h2 className="text-lg font-semibold text-primary-900">{t('edit')}</h2>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const hasChanges =
                (name && name !== taskToUpdate.name) ||
                (taskDescription !== taskToUpdate.description) ||
                (dueDate && dueDate.getTime() !== new Date(taskToUpdate.dueDate!).getTime()) ||
                (editStatusId !== undefined && editStatusId !== taskToUpdate.status?.statusId);
              if (hasChanges) {
                taskUtils.updateTask(taskToUpdate.taskId!, name, taskDescription, dueDate, editStatusId, refreshTable);
                setUpdateModalOpen(false);
              }
            }}
            className="flex flex-col gap-5 p-8">
            <Labeled label={t('name')}>
              <Input
                type="text"
                value={name}
                placeholder={taskToUpdate.name}
                name="name"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  setName(e.currentTarget.value);
                }}
              />
            </Labeled>
            <Labeled label={t('description')}>
              <Input
                type="textarea"
                value={taskDescription}
                placeholder={t('descriptionPlaceholder')}
                name="description"
                className="resize-y"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  setTaskDescription(e.currentTarget.value);
                }}
              />
            </Labeled>
            <div className="flex w-full items-start gap-6 max-md:flex-col">
              <Labeled label={t('dueDate')}>
                <DatePicker date={dueDate} setDate={setDueDate} />
              </Labeled>
              <Labeled label={t('assignees')} className="flex-1">
                <AssigneesMenu
                  taskId={taskToUpdate.taskId}
                  assignees={taskToUpdate.assignees}
                  assignMember={(userId, taskId) =>
                    taskUtils.assignMember(userId, taskId, community?.id, refreshTable)
                  }
                  removeMember={(userId, taskId) =>
                    taskUtils.removeMember(userId, taskId, community?.id, refreshTable)
                  }
                />
              </Labeled>
            </div>
            <Labeled label={t('status')}>
              <StatusPicker
                statuses={statuses}
                selectedStatusId={editStatusId}
                onSelect={(id) => setEditStatusId(id)}
                onManage={() => setManageStatusesOpen(true)}
              />
            </Labeled>
            <div className="flex items-center justify-between border-t border-secondary-200 pt-4">
              <Button
                type="button"
                variant="danger-v2"
                onClick={() => {
                  taskUtils.deleteTask(taskToUpdate.taskId!, refreshTable);
                  setUpdateModalOpen(false);
                }}>
                <TbTrash className="h-4 w-4" />
                {t('delete')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  !(
                    (name && name !== taskToUpdate.name) ||
                    (taskDescription !== taskToUpdate.description) ||
                    (dueDate && dueDate.getTime() !== new Date(taskToUpdate.dueDate!).getTime()) ||
                    (editStatusId !== undefined && editStatusId !== taskToUpdate.status?.statusId)
                  )
                }>
                {t('update')}
              </Button>
            </div>
          </form>
          <ManageStatusesModal
            open={manageStatusesOpen}
            setOpen={setManageStatusesOpen}
            statuses={statuses}
            tasks={taskList}
            onCreate={(statusName, color) => createStatus(statusName, color)}
            onUpdate={(id, data) => updateStatus(id, data)}
            onDelete={(id) => deleteStatus(id)}
            onReorder={(id, order) => updateStatus(id, { order })}
            onReload={reload}
          />
        </ModalLayout>
      )}

      {/* Manage Statuses modal (standalone, opened from page header) */}
      {!open && !updateModalOpen && (
        <ManageStatusesModal
          open={manageStatusesOpen}
          setOpen={setManageStatusesOpen}
          statuses={statuses}
          tasks={taskList}
          onCreate={(statusName, color) => createStatus(statusName, color)}
          onUpdate={(id, data) => updateStatus(id, data)}
          onDelete={(id) => deleteStatus(id)}
          onReorder={(id, order) => updateStatus(id, { order })}
          onReload={reload}
        />
      )}

      <PageWithTabsLayout<ViewMode>
        tabs={['list', 'board']}
        activeTab={viewMode}
        setActiveTab={setViewMode}
        tabLabels={{ list: t('listView'), board: t('boardView') }}
        renderTabContent={(tab) => {
          if (tab === 'board') {
            return taskList.length === 0 ? (
              <EmptyScreenView />
            ) : (
              <TaskBoard
                tasks={visibleTasks}
                statuses={visibleStatuses}
                communityId={community?.id}
                onMove={() => refreshTable()}
                handleUpdate={(task: Task) => {
                  setTaskToUpdate(task);
                  setUpdateModalOpen(true);
                }}
                refreshTable={refreshTable}
              />
            );
          }
          return taskList.length === 0 ? (
            <EmptyScreenView />
          ) : (
            <DndContext
              sensors={listSensors}
              collisionDetection={pointerWithin}
              onDragStart={(event: DragStartEvent) => {
                const found = visibleTasks.find((tsk) => String(tsk.taskId) === String(event.active.id));
                setActiveListTask(found ?? null);
              }}
              onDragEnd={async (event: DragEndEvent) => {
                setActiveListTask(null);
                const { active, over } = event;
                if (!over || !community?.id) return;
                const task = visibleTasks.find((tsk) => String(tsk.taskId) === String(active.id));
                if (!task) return;
                const targetStatusId = Number(over.id);
                if (task.status?.statusId === targetStatusId) return;
                const targetStatus = statuses.find((s) => s.statusId === targetStatusId);
                if (!targetStatus) return;
                const updatedTask = { ...task, status: targetStatus };
                setVisibleTasks((prev) => prev.map((taskDnd) => taskDnd.taskId === updatedTask.taskId ? updatedTask : taskDnd));
                setTaskList((prev) => prev.map((taskDnd) => taskDnd.taskId === updatedTask.taskId ? updatedTask : taskDnd));
                await taskService.moveToStatus(task, targetStatus, community.id);
                refreshTable();
              }}>
              <div className="flex w-full flex-col gap-6">
                {visibleStatuses.map((status) => (
                  <TaskSection
                    key={status.statusId}
                    statusId={status.statusId}
                    label={getStatusLabel(status, t)}
                    statusColor={status.color}
                    tasks={visibleTasks.filter((x) => x.status?.statusId === status.statusId)}
                    refreshTable={refreshTable}
                    handleUpdate={(task: Task) => {
                      setTaskToUpdate(task);
                      setUpdateModalOpen(true);
                    }}
                    statuses={statuses}
                    communityId={community?.id}
                  />
                ))}
              </div>
              <DragOverlay>
                {activeListTask && (
                  <div className="rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3 shadow-lg text-sm font-medium text-primary-900 opacity-90">
                    {activeListTask.name}
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          );
        }}
      />
    </>
  );
}

function TaskSection({
  statusId,
  label,
  statusColor,
  tasks,
  defaultOpen = true,
  refreshTable,
  handleUpdate,
  statuses,
  communityId,
}: {
  statusId?: number;
  label: string;
  statusColor?: string;
  tasks: Task[];
  defaultOpen?: boolean;
  refreshTable: () => void;
  handleUpdate: (task: Task) => void;
  statuses: TaskStatus[];
  communityId?: number;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isMd = useIsScreenSize('md');
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const { setNodeRef, isOver } = useDroppable({ id: String(statusId ?? 'none') });

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        'overflow-hidden rounded-[12px] border border-secondary-200 bg-surface transition-colors',
        isOver && 'ring-2 ring-primary-100',
      )}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between bg-primary-100 px-4 py-2.5 transition-colors hover:bg-primary-100/80">
        <div className="flex items-center gap-3">
          {statusColor && (
            <span
              className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: statusColor }}
            />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-primary-900">{label}</span>
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-surface px-1.5 text-xs font-semibold text-primary-900">
            {tasks.length}
          </span>
        </div>
        <TbChevronDown
          className={twMerge(
            'h-4 w-4 text-primary-900 transition-transform duration-200',
            !isOpen && '-rotate-90',
          )}
        />
      </button>

      {isOpen &&
        (isMd ? (
          <div className="flex flex-col gap-2 p-3">
            {tasks.length === 0 ? (
              <p className="py-2 text-center text-sm text-gray-500">{t('noTasks')}</p>
            ) : (
              tasks.map((task) => (
                <TaskCard
                  key={task.taskId}
                  task={task}
                  refreshTable={refreshTable}
                  handleUpdate={() => handleUpdate(task)}
                  statuses={statuses}
                  communityId={communityId}
                />
              ))
            )}
          </div>
        ) : (
          <TaskTable
            tasks={tasks}
            refreshTable={refreshTable}
            handleUpdate={(task) => handleUpdate(task)}
            statuses={statuses}
            communityId={communityId}
          />
        ))}
    </div>
  );
}

function TaskTableRow({
  task,
  refreshTable,
  handleUpdate,
  statuses,
  communityId,
}: {
  task: Task;
  refreshTable: () => void;
  handleUpdate: () => void;
  statuses: TaskStatus[];
  communityId?: number;
}) {
  const community = useSelectedCommunity();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(task.taskId),
    data: { task },
  });

  return (
    <tr
      ref={setNodeRef}
      className={twMerge(
        'cursor-pointer rounded-[8px] bg-secondary-200/70 hover:bg-secondary-200',
        isDragging ? 'opacity-0' : '',
      )}
      onClick={handleUpdate}>
      <td className="rounded-l-[8px] px-1 py-2.5 w-6" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="cursor-grab touch-none text-gray-300 active:cursor-grabbing"
          {...attributes}
          {...listeners}>
          <TbGripVertical className="h-3.5 w-3.5" />
        </button>
      </td>
      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
        <StatusSelector
          task={task}
          statuses={statuses}
          communityId={communityId}
          onChange={refreshTable}
        />
      </td>

      <td className="px-2 py-2.5">
        <p className="truncate text-sm font-semibold text-primary-900">
          {task.name}
        </p>
      </td>

      <td className="px-2 py-2.5">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <TbCalendar className="h-3 w-3 flex-shrink-0" />
          <span className="whitespace-nowrap">{task.dueDate ? formatDateAndTime(new Date(task.dueDate)) : '—'}</span>
        </div>
      </td>

      <td className="rounded-r-[8px] px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
        <AssigneesMenu
          taskId={task.taskId}
          assignees={task.assignees}
          assignMember={(userId, tId) =>
            taskUtils.assignMember(userId, tId, community?.id, refreshTable)
          }
          removeMember={(userId, tId) =>
            taskUtils.removeMember(userId, tId, community?.id, refreshTable)
          }
        />
      </td>
    </tr>
  );
}

function TaskCard({
  task,
  refreshTable,
  handleUpdate,
  statuses,
  communityId,
}: {
  task: Task;
  refreshTable: () => void;
  handleUpdate: () => void;
  statuses: TaskStatus[];
  communityId?: number;
}) {
  const community = useSelectedCommunity();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(task.taskId),
    data: { task },
  });

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        'flex cursor-pointer items-center gap-2 rounded-[8px] bg-secondary-200/70 px-3 py-2.5 hover:bg-secondary-200',
        isDragging && 'opacity-0',
      )}
      onClick={handleUpdate}>
      <button
        type="button"
        className="cursor-grab touch-none text-gray-300 active:cursor-grabbing flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}>
        <TbGripVertical className="h-4 w-4" />
      </button>
      <div onClick={(e) => e.stopPropagation()}>
        <StatusSelector
          task={task}
          statuses={statuses}
          communityId={communityId}
          onChange={refreshTable}
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-primary-900">
          {task.name}
        </p>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <TbCalendar className="h-3 w-3" />
          <span>{task.dueDate ? formatDateAndTime(new Date(task.dueDate)) : '—'}</span>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <AssigneesMenu
          taskId={task.taskId}
          assignees={task.assignees}
          assignMember={(userId, tId) =>
            taskUtils.assignMember(userId, tId, community?.id, refreshTable)
          }
          removeMember={(userId, tId) =>
            taskUtils.removeMember(userId, tId, community?.id, refreshTable)
          }
        />
      </div>
    </div>
  );
}

function AssigneesMenu({
  taskId,
  assignees,
  assignMember,
  removeMember,
}: {
  taskId?: number;
  assignees: { userId: number; firstName: string; lastName: string }[];
  assignMember: (userId: number, taskId?: number) => void;
  removeMember: (userId: number, taskId?: number) => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.MEMBER,
  );

  return (
    <div className="relative flex min-h-7 items-center">
      <button type="button" onClick={() => setModalOpen(true)} className="flex items-center">
        {assignees.length > 0 ? (
          assignees
            .slice(0, 2)
            .map((assignee, index) => {
              const connection = connections.find((c) => c.memberId === assignee.userId);
              return (
                <Avatar
                  key={assignee.userId}
                  className={`relative z-[${10 - index}] flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-primary-900 ${index === 0 ? '' : '-ml-1.5'}`}
                  src={connection?.picture}
                  alias={`${assignee.firstName}+${assignee.lastName}`}
                />
              );
            })
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-200 text-xs text-gray-800">
            <FaPlus />
          </div>
        )}
        {assignees.length > 2 && (
          <div
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-xs font-bold text-white shadow-md"
            style={{ marginLeft: '-6px', zIndex: 1 }}>
            +{assignees.length - 2}
          </div>
        )}
      </button>
      <ModalLayout
        closeButton
        open={modalOpen}
        setOpen={setModalOpen}
        className="z-50 mx-6 my-8 w-full overflow-hidden rounded-2xl bg-secondary-50 shadow-xl sm:max-w-[480px]">
        <AssignUnassignContent
          taskId={taskId}
          assignedUsers={assignees}
          allUsers={connections}
          assignMember={assignMember}
          removeMember={removeMember}
          onClose={() => setModalOpen(false)}
        />
      </ModalLayout>
    </div>
  );
}

interface AssignUnassignContentProps {
  taskId?: number;
  assignedUsers: { userId: number; firstName: string; lastName: string }[];
  allUsers: Connection[];
  assignMember: (userId: number, taskId?: number) => void;
  removeMember: (userId: number, taskId?: number) => void;
  onClose: () => void;
}

function AssignUnassignContent({
  taskId,
  assignedUsers,
  allUsers,
  assignMember,
  removeMember,
  onClose,
}: AssignUnassignContentProps): JSX.Element {
  const [inputValue, setInputValue] = useState<string>('');
  const [visibleUsers, setVisibleUsers] = useState<Connection[]>([]);

  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });

  useEffect(() => {
    setVisibleUsers(allUsers.filter((x) => !assignedUsers.some((a) => a.userId === x.memberId)));
  }, [assignedUsers, allUsers]);

  const handleInputChange = (e: string): void => {
    setInputValue(e);
    setVisibleUsers(
      allUsers.filter(
        (user) =>
          !assignedUsers.some((a) => a.userId === user.memberId) &&
          (user.fields.FIRST_NAME.toLowerCase().includes(e) ||
            user.fields.LAST_NAME.toLowerCase().includes(e)),
      ),
    );
  };

  const handleAssign = async (userId: number): Promise<void> => {
    assignMember(userId, taskId);
    setInputValue('');
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b border-secondary-200 bg-secondary-50 px-6 py-4">
        <FaUsers className="h-4 w-4 text-primary-900" />
        <span className="text-base font-semibold text-primary-900">{t('assignees')}</span>
      </div>
      <div className="flex flex-col gap-4 p-6">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <HiSearch className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('searchUsers')}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value.toLowerCase())}
            className="w-full rounded-lg border border-secondary-200 py-2 pl-10 pr-3 text-sm text-primary-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-900-300"
          />
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-900-300">
            {t('assignedUsers')}
          </p>
          <div className="no-scrollbar max-h-[140px] overflow-y-auto">
            {assignedUsers.length === 0 ? (
              <p className="text-xs text-gray-500">{t('noAssignees')}</p>
            ) : (
              assignedUsers.map((user) => (
                <div
                  key={user.userId}
                  className="mb-1 flex items-center justify-between rounded-lg bg-secondary-50 px-3 py-2 text-sm text-primary-900">
                  <span>
                    {user.firstName} {user.lastName}
                  </span>
                  <Button
                    type="button"
                    onClick={() => removeMember(user.userId!, taskId)}
                    className="text-primary-900-300 focus:outline-none">
                    <HiX className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-900-300">
            {t('unassignedUsers')}
          </p>
          <div className="no-scrollbar max-h-[140px] overflow-y-auto">
            {visibleUsers.length === 0 ? (
              <p className="text-xs text-gray-500">{t('noUsersToAssign')}</p>
            ) : (
              visibleUsers.map((user) => (
                <div
                  key={user.memberId}
                  className="mb-1 flex items-center justify-between rounded-lg px-3 py-2 text-sm text-primary-900">
                  <span>
                    {user.fields.FIRST_NAME} {user.fields.LAST_NAME}
                  </span>
                  <Button
                    type="button"
                    onClick={() => handleAssign(user.memberId!)}
                    className="text-primary-900-300 focus:outline-none">
                    <FaPlus />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end border-t border-secondary-200 px-6 py-4">
        <Button onClick={onClose} variant="secondary">
          {t('continue')}
        </Button>
      </div>
    </>
  );
}

function TaskTable({
  tasks,
  refreshTable,
  handleUpdate,
  statuses,
  communityId,
}: {
  tasks: Task[];
  refreshTable: () => void;
  handleUpdate: (task: Task) => void;
  statuses: TaskStatus[];
  communityId?: number;
}) {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });

  return (
    <div className="overflow-visible p-3">
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <th className="w-[4%] px-1 py-1.5" />
            <th className="w-[5%] px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('status')}
            </th>
            <th className="w-[40%] px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('task')}
            </th>
            <th className="w-[18%] px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('dueDate')}
            </th>
            <th className="w-[15%] px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('assignees')}
            </th>
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                {t('noTasks')}
              </td>
            </tr>
          ) : (
            tasks.map((task) => (
              <TaskTableRow
                task={task}
                key={task.taskId}
                refreshTable={refreshTable}
                handleUpdate={() => handleUpdate(task)}
                statuses={statuses}
                communityId={communityId}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function StatusPicker({
  statuses,
  selectedStatusId,
  onSelect,
  onManage,
}: {
  statuses: TaskStatus[];
  selectedStatusId?: number;
  onSelect: (id: number | undefined) => void;
  onManage: () => void;
}) {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const [showDropdown, setShowDropdown] = useState(false);

  const selectedStatus = statuses.find((s) => s.statusId === selectedStatusId);

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <button
          type="button"
          onClick={() => setShowDropdown((v) => !v)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          className="flex w-full gap-2 px-2 items-center h-[50px] rounded-[8px] border-2 border-secondary-200 border-opacity-50 bg-secondary-200 bg-opacity-50 font-medium text-primary-900 transition-colors focus:border-primary-300 focus:border-opacity-100 focus:ring-0;">
          {selectedStatus ? (
            <>
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: selectedStatus.color }}
              />
              <span className="flex-1 text-left whitespace-nowrap overflow-hidden overflow-ellipsis">
                {getStatusLabel(selectedStatus, t)}
              </span>
            </>
          ) : (
            <span className="flex-1 text-left text-gray-400 whitespace-nowrap overflow-hidden overflow-ellipsis">
              {t('statusPlaceholder')}
            </span>
          )}
          <TbChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
        </button>
        {showDropdown && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-secondary-200 bg-secondary-50 shadow-lg">
            {statuses.map((status) => (
              <button
                key={status.statusId}
                type="button"
                onMouseDown={() => { onSelect(status.statusId); setShowDropdown(false); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary-900 hover:bg-secondary-200">
                <span
                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: status.color }}
                />
                {getStatusLabel(status, t)}
              </button>
            ))}
          </div>
        )}
      </div>
      <Button
        variant="secondary"
        className='rounded-full w-[50px] h-[50px]'
        title={t('manageStatuses')}
        onClick={onManage}>
        <TbEdit />
      </Button>
    </div>
  );
}


// component responsible for showing the status color and allowing inline change
function StatusSelector({
  task,
  statuses,
  communityId,
  onChange,
}: {
  task: Task;
  statuses: TaskStatus[];
  communityId?: number;
  onChange: () => void;
}) {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const selected = statuses.find((s) => s.statusId === task.status?.statusId);

  useEffect(() => {
    if (!open) {
      return () => {};
    }
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen((v) => !v);
  };

  const handleSelect = async (status?: TaskStatus) => {
    if (task.taskId && communityId) {
      if (status) {
        await taskService.moveToStatus(task, status, communityId);
      }
      setOpen(false);
      onChange();
    }
  };

  return (
    <div>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className="h-5 w-5 flex-shrink-0 rounded-full border border-secondary-200"
        style={{ backgroundColor: selected?.color || '#6B7280' }}
      />
      {open && dropdownPos &&
        createPortal(
          <div
            style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
            className="min-w-[6rem] py-2 w-auto overflow-hidden rounded-lg border border-secondary-200 bg-secondary-50 shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}>
            {statuses.map((s) => (
              <button
                key={s.statusId}
                type="button"
                onMouseDown={() => handleSelect(s)}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary-900 hover:bg-secondary-200">
                <span
                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                {getStatusLabel(s, t)}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

const taskUtils = {
  assignMember: (userId: number, taskId?: number, communityId?: number, cb?: () => void) => {
    if (communityId) {
      taskService.assignMemberToTask(taskId!, userId, communityId).then(cb);
    }
  },
  removeMember: (userId: number, taskId?: number, communityId?: number, cb?: () => void) => {
    if (communityId) {
      taskService.removeMemberFromTask(taskId!, userId, communityId).then(cb);
    }
  },
  updateTask: (taskId: number, name?: string, description?: string, dueDate?: Date, statusId?: number, cb?: () => void) => {
    taskService
      .updateTask(taskId, {
        name,
        description,
        dueDate,
        statusId,
      })
      .then(cb);
  },
  deleteTask: (taskId: number, cb?: () => void) => {
    taskService.deleteTask(taskId).then(cb);
  },
};

const STATUS_COLORS = [
  '#3B82F6', '#6B7280', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#06B6D4',
];

function ManageStatusesModal({
  open,
  setOpen,
  statuses,
  tasks,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
  onReload,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  statuses: TaskStatus[];
  tasks: Task[];
  onCreate: (name: string, color?: string) => Promise<TaskStatus>;
  onUpdate: (id: number, data: { name?: string; color?: string }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onReorder: (id: number, order: number) => Promise<void>;
  onReload: () => void;
}) {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.task' });
  const [newName, setNewName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState(STATUS_COLORS[0]);
  const [showNewSwatches, setShowNewSwatches] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);
  // Local copy for optimistic reorder
  const [localStatuses, setLocalStatuses] = useState<TaskStatus[]>([]);
  const suppressSync = useRef(false);

  useEffect(() => {
    if (suppressSync.current) return;
    setLocalStatuses([...statuses].sort((a, b) => a.order - b.order));
  }, [statuses]);

  useEffect(() => {
    if (!open) {
      setNewName('');
      setNewStatusColor(STATUS_COLORS[0]);
      setEditingId(null);
      setEditingName('');
    }
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localStatuses.findIndex((s) => String(s.statusId) === String(active.id));
    const newIndex = localStatuses.findIndex((s) => String(s.statusId) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(localStatuses, oldIndex, newIndex);
    setLocalStatuses(reordered);
    suppressSync.current = true;
    await Promise.all(reordered.map((status, idx) => onReorder(status.statusId, idx)));
    suppressSync.current = false;
    onReload();
  };

  const handleColorChange = async (id: number, color: string) => {
    const status = localStatuses.find((s) => s.statusId === id);
    if (status && isSystemStatus(status)) return;
    setLocalStatuses((prev) => prev.map((s) => (s.statusId === id ? { ...s, color } : s)));
    await onUpdate(id, { color });
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await onCreate(newName.trim(), newStatusColor);
      setNewName('');
      setNewStatusColor(STATUS_COLORS[0]);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id: number) => {
    const status = localStatuses.find((s) => s.statusId === id);
    if (status && isSystemStatus(status)) {
      setEditingId(null);
      return;
    }
    if (!editingName.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await onUpdate(id, { name: editingName.trim() });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    const status = localStatuses.find((s) => s.statusId === id);
    if (status && isSystemStatus(status)) {
      toast.error(t('defaultStatusLocked'));
      return;
    }
    if (tasks.some((task) => task.status?.statusId === id)) {
      toast.error(t('statusHasTasks'));
      return;
    }
    setSaving(true);
    try {
      await onDelete(id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalLayout
      closeButton
      open={open}
      setOpen={setOpen}
      className="z-50 mx-6 my-8 w-full rounded-2xl bg-secondary-50 sm:max-w-[480px]">
      <div className="border-b border-secondary-200 px-8 py-5">
        <h2 className="text-lg font-semibold text-primary-900">{t('manageStatuses')}</h2>
      </div>
      <div className="flex flex-col gap-4 p-8">
        {/* Sortable status list */}
        {localStatuses.length === 0 ? (
          <p className="text-sm text-gray-400">{t('noStatusesYet')}</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={localStatuses.map((s) => String(s.statusId))} strategy={verticalListSortingStrategy}>
              <ul className="flex flex-col gap-2">
                {localStatuses.map((status) => (
                  <SortableStatusRow
                    key={status.statusId}
                    status={status}
                    statusLabel={getStatusLabel(status, t)}
                    isSystem={isSystemStatus(status)}
                    isEditing={editingId === status.statusId}
                    editingName={editingName}
                    saving={saving}
                    onStartEdit={() => { if (!isSystemStatus(status)) { setEditingId(status.statusId); setEditingName(status.name); } }}
                    onEditChange={setEditingName}
                    onRename={() => handleRename(status.statusId)}
                    onCancelEdit={() => setEditingId(null)}
                    onDelete={() => handleDelete(status.statusId)}
                    onColorChange={(id, color) => handleColorChange(id, color)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}

        {/* Add new status */}
        <div className="flex items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2">
          <div
            className="relative"
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowNewSwatches(false); }}>
            <button
              type="button"
              className="h-5 w-5 flex-shrink-0 rounded-full border border-secondary-200 focus:outline-none"
              style={{ backgroundColor: newStatusColor }}
              title={t('pickColor')}
              onClick={() => setShowNewSwatches((v) => !v)}
            />
            {showNewSwatches && (
              <div className="absolute bottom-7 left-0 z-50 flex flex-wrap gap-1 rounded-lg border border-secondary-200 bg-secondary-50 p-2 shadow-lg" style={{ width: 116 }}>
                {STATUS_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={twMerge('h-5 w-5 rounded-full border-2 focus:outline-none', c === newStatusColor ? 'border-primary-900' : 'border-transparent hover:border-secondary-200')}
                    style={{ backgroundColor: c }}
                    onClick={() => { setNewStatusColor(c); setShowNewSwatches(false); }}
                  />
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            className="flex-1 bg-transparent text-sm text-primary-900 placeholder-gray-400 focus:outline-none"
            placeholder={t('newStatusPlaceholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }}
          />
          <button
            type="button"
            disabled={saving || !newName.trim()}
            onClick={handleCreate}
            className="flex items-center gap-1 rounded-lg bg-primary px-3 py-3 text-sm font-semibold text-white disabled:opacity-40">
            <FaPlus className="h-3 w-3" />
            {t('addStatus')}
          </button>
        </div>
      </div>
    </ModalLayout>
  );
}

function SortableStatusRow({
  status,
  statusLabel,
  isSystem,
  isEditing,
  editingName,
  saving,
  onStartEdit,
  onEditChange,
  onRename,
  onCancelEdit,
  onDelete,
  onColorChange,
}: {
  status: TaskStatus;
  statusLabel: string;
  isSystem: boolean;
  isEditing: boolean;
  editingName: string;
  saving: boolean;
  onStartEdit: () => void;
  onEditChange: (v: string) => void;
  onRename: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onColorChange: (id: number, color: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(status.statusId),
  });
  const style = { transform: DndCSS.Transform.toString(transform), transition };
  const [showSwatches, setShowSwatches] = useState(false);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={twMerge(
        'flex items-center gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2',
        isDragging && 'opacity-40',
      )}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className={twMerge(
          'touch-none text-gray-300',
          'cursor-grab active:cursor-grabbing',
        )}
        title="Drag to reorder">
        <TbGripVertical className="h-4 w-4" />
      </button>
      <div
        className="relative"
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowSwatches(false); }}>
        <button
          type="button"
          className={twMerge('h-3 w-3 flex-shrink-0 rounded-full focus:outline-none', isSystem && 'cursor-not-allowed')}
          style={{ backgroundColor: status.color }}
          title="Change colour"
          disabled={isSystem}
          onClick={() => setShowSwatches((v) => !v)}
        />
        {showSwatches && (
          <div className="absolute left-0 top-5 z-50 flex flex-wrap gap-1 rounded-lg border border-secondary-200 bg-secondary-50 p-2 shadow-lg" style={{ width: 116 }}>
            {STATUS_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={twMerge('h-5 w-5 rounded-full border-2 focus:outline-none', c === status.color ? 'border-primary-900' : 'border-transparent hover:border-secondary-200')}
                style={{ backgroundColor: c }}
                onClick={() => { onColorChange(status.statusId, c); setShowSwatches(false); }}
              />
            ))}
          </div>
        )}
      </div>
      {isEditing ? (
        <input
          type="text"
          ref={(el) => { if (el && isEditing) { el.focus(); } }}
          className="flex-1 bg-transparent text-sm text-primary-900 placeholder-gray-400 focus:outline-none"
          value={editingName}
          onChange={(e) => onEditChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onRename();
            if (e.key === 'Escape') onCancelEdit();
          }}
          onBlur={onRename}
        />
      ) : (
        <span className="flex-1 text-sm text-primary-900">{statusLabel}</span>
      )}
      <button
        type="button"
        disabled={saving || isSystem}
        onClick={onStartEdit}
        className="rounded p-1 text-gray-400 hover:bg-secondary-200 hover:text-primary-900 disabled:cursor-not-allowed disabled:opacity-40">
        <TbEdit className="h-4 w-4" />
      </button>
      <button
        type="button"
        disabled={saving || isSystem}
        onClick={onDelete}
        className="rounded p-1 text-gray-400 hover:bg-secondary-200 hover:text-danger disabled:cursor-not-allowed disabled:opacity-40">
        <TbTrash className="h-4 w-4" />
      </button>
    </li>
  );
}
